import React from "react";
import { Navigate } from "react-router-dom";
import { useAdvertiser } from "../contexts/AdvertiserContext";
import { resolveVerticalDashboardConfig } from "../config/verticalDashboardConfig";

// Storage keys mirror AdvertiserContext. The raw stored vertical is the only
// signal that distinguishes "vertical known" from the context's normalized
// "general" fallback, so the guard fails open when it is absent rather than
// bouncing an advertiser whose classification never got persisted.
const KEY_VERTICAL = "selectedAdvertiserVertical";

function readStoredVertical() {
  try {
    return String(localStorage.getItem(KEY_VERTICAL) || "").trim();
  } catch {
    return "";
  }
}

/**
 * Pure eligibility decision for a vertical-restricted intelligence route.
 * A route is eligible only when it is the route the governed vertical registry
 * (VERTICAL_DASHBOARD_CONFIG) assigns to the current advertiser's vertical —
 * the same resolution the Dashboard vertical tile uses, so page visibility,
 * route access, and gesture adjacency all agree.
 */
export function resolveVerticalIntelligenceAccess({
  routePath,
  advertiserPresent,
  verticalKnown,
  advertiserVertical,
}) {
  if (!advertiserPresent || !verticalKnown) {
    return { allowed: true, redirectTo: null };
  }
  const eligibleRoute = resolveVerticalDashboardConfig(advertiserVertical).route;
  if (eligibleRoute === routePath) {
    return { allowed: true, redirectTo: null };
  }
  return { allowed: false, redirectTo: eligibleRoute };
}

/**
 * Route-level backstop for vertical-restricted intelligence pages. Redirects
 * an ineligible advertiser to their own vertical's intelligence route before
 * the restricted page renders — deep links and history navigation cannot
 * bypass the governed gesture sequence.
 */
export default function VerticalIntelligenceRoute({ routePath, children }) {
  const advCtx = useAdvertiser() || {};

  // Hold rendering for the single pre-hydration frame: the context's vertical
  // is a placeholder until localStorage hydration runs, and deciding against
  // it would either flash restricted content or misroute a valid advertiser.
  if (!advCtx.advertiserHydrated) return null;

  const advertiserPresent = Boolean(
    advCtx.selectedAdvertiserId || advCtx.selectedAdvertiserName
  );
  const storedVertical = readStoredVertical();
  const access = resolveVerticalIntelligenceAccess({
    routePath,
    advertiserPresent,
    verticalKnown: Boolean(storedVertical),
    advertiserVertical: advCtx.selectedAdvertiserVertical || storedVertical,
  });

  if (!access.allowed) {
    return <Navigate to={access.redirectTo} replace />;
  }
  return children;
}
